import { Box, Typography, Stack, Paper, useTheme, Skeleton } from '@mui/material';
import { formatDollars, titleCase } from '@/utils/helperFunctions';
import { EmptyStateView } from '@/components/Common/EmptyStateView';

export default function IntegrationUsage({ data, isLoading }: { data: any[]; isLoading: boolean }) {
  const theme = useTheme();

  if (isLoading) {
    return <IntegrationUsageSkeleton />;
  }

  const integrations = data?.map((usage: any) => ({
    name: titleCase(usage.name),
    price: `${formatDollars(usage.unit_amount)}`,
    credentials: usage.quantity,
    total: `${formatDollars(usage.estimated_amount)}`
  }));

  return (
    <Box>
      <Typography variant='h6'>Integration Usage Details</Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        mb={2}
        mt={0.5}
        gutterBottom
        fontSize={16}
        sx={{ display: { xs: 'none', sm: 'block' } }}
      >
        Current Month integration costs
      </Typography>

      <Stack spacing={2}>
        {integrations?.length ? (
          integrations.map((integration: any) => (
            <Paper
              key={integration.name}
              sx={{
                p: { xs: 1.5, lg: 2 },
                bgcolor: theme.palette.primary.light,
                borderRadius: 1.5,
                boxShadow: 'none'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={{ xs: 1, sm: 0 }}
              >
                <Typography flex={1}>
                  <Typography fontWeight={500}>{integration.name}</Typography>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    {integration.price} (integration price) × {integration.credentials} credentials
                  </Typography>
                </Typography>
                <Typography fontWeight='bold' sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
                  {integration.total}
                </Typography>
              </Stack>
            </Paper>
          ))
        ) : (
          <EmptyStateView message='No Integration Found' />
        )}
      </Stack>
    </Box>
  );
}

function IntegrationUsageSkeleton() {
  const theme = useTheme();

  return (
    <Box marginX={2}>
      <Skeleton variant='text' width={220} height={28} />
      <Skeleton variant='text' width={300} height={18} sx={{ mb: 2 }} />

      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <Paper
            key={i}
            sx={{
              p: 2,
              bgcolor: theme.palette.primary.light,
              borderRadius: 1.5
            }}
          >
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Box>
                <Skeleton variant='text' width={120} height={20} />
                <Skeleton variant='text' width={200} height={16} />
              </Box>
              <Skeleton variant='text' width={80} height={20} />
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
